import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Table } from '@/components/ui/Table'
import { FormField } from '@/components/ui/Form'
import { AlertModal } from '@/components/ui/AlertModal'
import { ConfirmModal } from '@/components/ui/ConfirmModal'
import { billingService } from '@/services/billing.service'
import type { CompanySubscription, SubscriptionPlan } from '@/types/billing.types'

interface Props {
  companyId: string
}

export function CompanySubscriptionManager({ companyId }: Props) {
  const navigate = useNavigate()
  const [history, setHistory] = useState<CompanySubscription[]>([])
  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [loading, setLoading] = useState(true)
  const [assigning, setAssigning] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  // Modals logic
  const [showModal, setShowModal] = useState(false)
  const [selectedPlanId, setSelectedPlanId] = useState('')
  const [selectedBillingCycle, setSelectedBillingCycle] = useState<'MONTHLY' | 'YEARLY'>('MONTHLY')
  const [spendingLimit, setSpendingLimit] = useState<number>(0)
  const [requirePaymentGateway, setRequirePaymentGateway] = useState(true)
  const [expiresAt, setExpiresAt] = useState('')

  const [confirmConfig, setConfirmConfig] = useState<{ isOpen: boolean; message: string; onConfirm: () => void } | null>(null)
  const [alertConfig, setAlertConfig] = useState<{ isOpen: boolean; message: string } | null>(null)

  useEffect(() => {
    fetchData()
  }, [companyId])

  async function fetchData() {
    try {
      setLoading(true)
      setErrorMsg('')
      const [hist, allPlans] = await Promise.all([
        billingService.adminListCompanySubscriptions(companyId),
        billingService.getAvailablePlans(companyId)
      ])
      setHistory(hist)
      setPlans(allPlans)
    } catch (err: any) {
      console.error(err)
      setErrorMsg(err.response?.data?.message || err.message || 'Erro ao carregar assinaturas')
    } finally {
      setLoading(false)
    }
  }

  function closeAssignModal() {
    setShowModal(false)
    setSelectedPlanId('')
    setSelectedBillingCycle('MONTHLY')
    setSpendingLimit(0)
    setRequirePaymentGateway(true)
    setExpiresAt('')
  }

  async function handleAssignPlan() {
    if (!selectedPlanId) return

    let formattedExpiresAt: string | null = null
    if (!requirePaymentGateway) {
      if (expiresAt) {
        const selectedDate = new Date(expiresAt)
        if (selectedDate <= new Date()) {
          setAlertConfig({
            isOpen: true,
            message: 'A data de expiração/vencimento deve ser no futuro.'
          })
          return
        }
        formattedExpiresAt = selectedDate.toISOString()
      }
    }

    try {
      setAssigning(true)
      await billingService.adminAssignPlanToCompany(
        companyId,
        selectedPlanId,
        spendingLimit,
        requirePaymentGateway,
        formattedExpiresAt,
        selectedBillingCycle
      )
      closeAssignModal()
      await fetchData()
    } catch (err: any) {
      setAlertConfig({ isOpen: true, message: err.response?.data?.message || 'Erro ao atribuir plano' })
    } finally {
      setAssigning(false)
    }
  }

  function handleCancelActive() {
    setConfirmConfig({
      isOpen: true,
      message: 'Tem certeza que deseja cancelar o plano atual desta empresa? O acesso será revogado imediatamente.',
      onConfirm: async () => {
        try {
          setAssigning(true)
          await billingService.adminCancelCompanySubscription(companyId)
          await fetchData()
        } catch (err: any) {
          setAlertConfig({ isOpen: true, message: err.response?.data?.message || 'Erro ao cancelar assinatura' })
        } finally {
          setAssigning(false)
          setConfirmConfig(null)
        }
      }
    })
  }

  const activeSub = history.find(s => s.status === 'active')
  
  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
  }
  
  const formatDate = (isoStr: string | null) => {
    if (!isoStr) return '—'
    return new Date(isoStr).toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    })
  }

  return (
    <div style={{ marginTop: '2rem' }}>
      <Card>
        <Card.Header>
          Gestão de Assinaturas (Admin)
        </Card.Header>
        <Card.Body>
          {errorMsg ? (
            <div className="ui-alert ui-alert--danger" style={{ marginBottom: '1rem' }} role="alert">
              {errorMsg}
            </div>
          ) : loading ? (
            <div className="ui-empty-state" style={{ minHeight: 100 }}>Carregando assinaturas...</div>
          ) : (
            <>
              {/* STATUS ATUAL */}
              <div style={{ padding: '1.5rem', background: 'var(--surface-2)', borderRadius: 'var(--radius-lg)', marginBottom: '2rem', border: '1px solid var(--border)' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-1)', marginBottom: '1rem' }}>Plano Ativo</h3>
                {activeSub ? (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                    <div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--brand-primary)', marginBottom: '0.25rem' }}>
                        {activeSub.plan?.name || 'Plano Desconhecido'}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: 'var(--text-2)' }}>
                        Valor Mensalidade: <strong style={{ color: 'var(--text-1)' }}>{formatCurrency(activeSub.snapshot_price)}</strong>
                        {activeSub.snapshot_billing_cycle === 'YEARLY' || activeSub.billing_cycle === 'YEARLY' ? (
                          <span style={{ marginLeft: '6px', color: 'var(--bronze, #d97706)', fontWeight: 600 }}>(Ciclo Anual -20%)</span>
                        ) : (
                          <span style={{ marginLeft: '6px', color: 'var(--text-3)' }}>(Mensal)</span>
                        )} • 
                        Início: <strong style={{ color: 'var(--text-1)' }}>{formatDate(activeSub.start_date)}</strong>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Button variant="secondary" onClick={() => setShowModal(true)}>
                        Trocar Plano
                      </Button>
                      <Button variant="danger" onClick={() => void handleCancelActive()}>
                        Cancelar Assinatura
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                    <div style={{ color: 'var(--text-3)' }}>
                      Nenhum plano ativo no momento. A empresa está no Paywall.
                    </div>
                    <Button onClick={() => setShowModal(true)}>
                      Atribuir Plano
                    </Button>
                  </div>
                )}
              </div>

              {/* HISTÓRICO / TIMELINE */}
              <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-1)', marginBottom: '1rem' }}>Histórico de Assinaturas</h3>
              {history.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-3)', fontSize: '0.9rem' }}>
                  A empresa não possui histórico de planos.
                </div>
              ) : (
                <Table.Wrapper standalone>
                  <Table>
                    <thead>
                      <tr>
                        <th>Plano</th>
                        <th>Ciclo</th>
                        <th>Mensalidade (Snap)</th>
                        <th>Implantação (Snap)</th>
                        <th>Status</th>
                        <th>Data Início</th>
                        <th>Data Fim</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map(sub => (
                        <tr key={sub.id}>
                          <td style={{ fontWeight: 600, color: 'var(--text-1)' }}>{sub.plan?.name || '-'}</td>
                          <td>
                            <Table.Badge variant={sub.snapshot_billing_cycle === 'YEARLY' || sub.billing_cycle === 'YEARLY' ? 'warning' : 'info'}>
                              {sub.snapshot_billing_cycle === 'YEARLY' || sub.billing_cycle === 'YEARLY' ? 'Anual (-20%)' : 'Mensal'}
                            </Table.Badge>
                          </td>
                          <td>{formatCurrency(sub.snapshot_price)}</td>
                          <td>{formatCurrency(sub.snapshot_setup_fee || 0)}</td>
                          <td>
                            <Table.Badge variant={sub.status === 'active' ? 'success' : sub.status === 'canceled' ? 'danger' : 'warning'}>
                              {sub.status.toUpperCase()}
                            </Table.Badge>
                          </td>
                          <td>{formatDate(sub.start_date)}</td>
                          <td>{formatDate(sub.end_date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Table.Wrapper>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* MODAL DE TROCA DE PLANO NO PADRÃO DO DESIGN SYSTEM */}
      {showModal && (
        <div 
          className="ui-dialog-overlay" 
          onClick={closeAssignModal} 
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', backdropFilter: 'blur(3px)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', animation: 'fadeIn .15s ease' }}
          role="dialog"
          aria-modal="true"
        >
          <Card style={{ width: '100%', maxWidth: '500px', margin: 0, animation: 'slideUp .18s ease', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            <Card.Header>
              Atribuir Plano
            </Card.Header>
            <Card.Body style={{ padding: '24px' }}>
              <div className="ui-form">
                {plans.length === 0 ? (
                  <div className="ui-alert ui-alert--info" style={{ marginBottom: '1.5rem', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '1rem' }}>
                    <div style={{ fontWeight: 600, color: 'var(--text-1)', marginBottom: '0.25rem' }}>
                      Nenhum plano comercial disponível
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-2)', marginBottom: '1rem', lineHeight: '1.4' }}>
                      Não há planos comerciais cadastrados para esta organização. Crie um plano comercial no painel de planos antes de atribuí-lo a uma empresa.
                    </div>
                    <Button variant="secondary" size="sm" onClick={() => { closeAssignModal(); navigate('/dashboard/plans'); }}>
                      Ir para Planos Comerciais
                    </Button>
                  </div>
                ) : (
                  <>
                    <FormField label="Selecione o plano desejado" htmlFor="modal-plan">
                      <select 
                        id="modal-plan"
                        value={selectedPlanId}
                        onChange={e => setSelectedPlanId(e.target.value)}
                        style={{ width: '100%' }}
                      >
                        <option value="" disabled>-- Selecione --</option>
                        {plans.map(p => {
                          const banks = p.max_bank_accounts === -1 ? 'Bancos Ilimitados' : `${p.max_bank_accounts} ${p.max_bank_accounts === 1 ? 'banco' : 'bancos'}`
                          const payments = p.max_payments === -1 ? 'Despesas Ilimitadas' : `${p.max_payments} pagamentos`
                          return (
                            <option key={p.id} value={p.id}>
                              {p.name} — {formatCurrency(p.price)}/mês — {banks} | {payments}
                            </option>
                          )
                        })}
                      </select>
                    </FormField>

                    {selectedPlanId && (() => {
                      const chosenPlan = plans.find(p => p.id === selectedPlanId)
                      if (!chosenPlan || chosenPlan.allow_annual_billing === false) return null
                      return (
                        <FormField label="Ciclo de Cobrança" htmlFor="modal-cycle">
                          <select
                            id="modal-cycle"
                            value={selectedBillingCycle}
                            onChange={e => setSelectedBillingCycle(e.target.value as 'MONTHLY' | 'YEARLY')}
                            style={{ width: '100%' }}
                          >
                            <option value="MONTHLY">Mensal — {formatCurrency(chosenPlan.price)}/mês</option>
                            <option value="YEARLY">
                              Anual (-20% OFF) — {formatCurrency(chosenPlan.annual_price ?? (chosenPlan.price * 12 * 0.8))}/ano ({formatCurrency(chosenPlan.annual_monthly_equivalent ?? (chosenPlan.price * 0.8))}/mês)
                            </option>
                          </select>
                        </FormField>
                      )
                    })()}

                    <FormField label="Trava de Segurança (Excedentes em R$)" htmlFor="modal-spending">
                      <input 
                        type="number"
                        id="modal-spending"
                        className="ui-input"
                        step="0.01"
                        min="0"
                        placeholder="Ex: 50.00"
                        value={spendingLimit}
                        onChange={e => setSpendingLimit(parseFloat(e.target.value) || 0)}
                        style={{ width: '100%' }}
                      />
                      <small style={{ color: 'var(--text-3)', display: 'block', marginTop: '4px' }}>
                        Deixe 0 se quiser bloquear qualquer cobrança além da franquia (Hard Limit).
                      </small>
                    </FormField>

                    <FormField htmlFor="modal-require-payment">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none', color: 'var(--text-1)', fontWeight: 500 }}>
                        <input 
                          type="checkbox"
                          id="modal-require-payment"
                          checked={requirePaymentGateway}
                          onChange={e => {
                            const checked = e.target.checked
                            setRequirePaymentGateway(checked)
                            if (checked) {
                              setExpiresAt('') // Reseta a data se exigir pagamento for marcado
                            }
                          }}
                          style={{
                            accentColor: 'var(--primary)',
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer'
                          }}
                        />
                        Exigir Pagamento via Gateway (Asaas)
                      </label>
                    </FormField>

                    {!requirePaymentGateway && (
                      <FormField label="Data de Expiração/Vencimento" htmlFor="modal-expires-at">
                        <input 
                          type="datetime-local"
                          id="modal-expires-at"
                          className="ui-input"
                          value={expiresAt}
                          onChange={e => setExpiresAt(e.target.value)}
                          style={{ width: '100%' }}
                        />
                        <small style={{ color: 'var(--text-3)', display: 'block', marginTop: '4px' }}>
                          Defina a data e hora limite para que a assinatura cortesia expire.
                        </small>
                      </FormField>
                    )}
                  </>
                )}
              </div>
            </Card.Body>
            <div className="ui-actions" style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', background: 'var(--surface-2)', marginTop: 0, justifyContent: 'flex-end', display: 'flex', gap: '12px' }}>
              <Button variant="secondary" onClick={closeAssignModal}>Cancelar</Button>
              <Button disabled={!selectedPlanId || assigning || plans.length === 0} onClick={() => void handleAssignPlan()}>
                {assigning ? 'Processando...' : 'Confirmar e Assinar'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* COMPONENTES NATÍVOS DO DS */}
      {alertConfig?.isOpen && (
        <AlertModal
          title="Atenção"
          type="error"
          message={alertConfig.message}
          onClose={() => setAlertConfig(null)}
        />
      )}

      {confirmConfig?.isOpen && (
        <ConfirmModal
          title="Confirmação de Cancelamento"
          message={confirmConfig.message}
          type="error"
          onConfirm={() => void confirmConfig.onConfirm()}
          onCancel={() => setConfirmConfig(null)}
          confirmText="Sim, Cancelar Acesso"
          cancelText="Não"
          isDanger={true}
          isLoading={assigning}
        />
      )}
    </div>
  )
}
